import { ButtonLink, ExternalButtonLink } from '~/components/ui/Button.js';
import { Alert, EmptyState, Loading } from '~/components/ui/Feedback.js';
import { JobRow } from '~/components/public/JobRow.js';
import { PageMeta } from '~/components/PageMeta.js';
import { Section } from '~/components/Section.js';
import {
  ABOUT_BLURB,
  EMPTY_ROLES,
  HERO,
  SITE,
} from '~/content/site.js';
import { usePublicJobs } from '~/lib/queries.js';

function OpenRoles() {
  const { data, isPending, isError, refetch } = usePublicJobs();

  if (isPending) return <Loading label="Loading open roles" />;

  if (isError) {
    return (
      <Alert title="We could not load the open roles.">
        <p>This is a problem on our side, not yours.</p>
        <button type="button" onClick={() => void refetch()} className="mt-2 underline underline-offset-4">
          Try again
        </button>
      </Alert>
    );
  }

  if (data.items.length === 0) {
    return <EmptyState heading={EMPTY_ROLES.heading}>{EMPTY_ROLES.body}</EmptyState>;
  }

  return (
    <>
      <p className="mb-5 flex items-center gap-2 text-sm font-medium text-heading" aria-live="polite">
        <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
        {data.items.length} open {data.items.length === 1 ? 'role' : 'roles'}.
      </p>
      <ul className="border-y border-line-strong">
        {data.items.map((job) => (
          <JobRow key={job.slug} job={job} />
        ))}
      </ul>
    </>
  );
}

export function HomePage() {
  return (
    <>
      <PageMeta title={SITE.defaultTitle} description={SITE.defaultDescription} path="/" />

      <div className="mx-auto max-w-(--container-content) px-5 py-12 sm:px-8 sm:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <p className="section-label">{HERO.eyebrow}</p>
            <h1 className="mt-5 display-1">{HERO.heading}</h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-pretty text-muted">{HERO.intro}</p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <ButtonLink to={HERO.primaryCta.href}>{HERO.primaryCta.label}</ButtonLink>
              <ExternalButtonLink href={HERO.secondaryCta.href}>{HERO.secondaryCta.label}</ExternalButtonLink>
            </div>
          </div>
        </div>
      </div>

      <Section
        id="open-roles"
        label="Open positions"
        heading="Current opportunities."
        className="border-y border-line bg-surface"
      >
        <p className="mb-8 max-w-2xl text-base leading-relaxed text-muted">
          Every active opening is listed here. Choose a role to see the work, expectations, and application details.
        </p>
        <OpenRoles />
      </Section>

      <Section id="about" label="About the studio">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <p className="max-w-2xl text-lg leading-relaxed text-body lg:col-span-8">{ABOUT_BLURB}</p>
          <div className="lg:col-span-4">
            <a
              href={SITE.aboutUrl}
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-b border-line-strong pb-1 text-sm font-medium text-heading transition-colors hover:border-primary hover:text-primary"
            >
              More about Absurd Technologies
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </Section>
    </>
  );
}
